import {
  NETCODE_V4_FIXED_DT_SECONDS,
  NETCODE_V4_MAX_CATCHUP_TICKS,
  NETCODE_V4_MAX_SEGMENTS_PER_SNAKE,
  normalizeRadians,
  SnakeNetFlagsV4,
  signedUint32Delta,
} from '@star-snake/shared'
import {
  combatLengthFromSnakeLength,
  decreaseSnakeLengthBySparks,
  economicSparksFromSnakeLength,
  snakeSegmentSpacingFromLength,
} from '@star-snake/shared'
import type {
  ChainPointV4,
  DecodedAssignSnakeV4,
  DecodedSelfChainSnapshotV4,
  DecodedSelfHeadStateV4,
} from '../protocol/BinaryDecoderV4'
import { ChainStoreV4 } from './ChainStoreV4'
import { calculateSpeedV4, DEFAULT_MOVEMENT_CONFIG_V4, limitedTurnV4, type MovementConfigV4 } from './PhysicsV4'

const BOOSTING_FLAG = SnakeNetFlagsV4.BOOSTING
const MAX_INPUT_HISTORY = 256
const SOFT_DRIFT_DISTANCE = 12
const HARD_DRIFT_DISTANCE = 48
const SOFT_DRIFT_BEARING = 4 * Math.PI / 180
const HARD_DRIFT_BEARING = 12 * Math.PI / 180
const DISPLAY_SMOOTHING = 0.25

export interface RecordedInputV4 {
  seq: number
  inputTick: number
  desiredHeading: number
  flags: number
}

export interface SelfHeadViewV4 {
  x: number
  z: number
  bearing: number
  length: number
  flags: number
}

export interface ReconcileResultV4 {
  kind: 'none' | 'continue' | 'soft-drift' | 'resync'
  headError: number
  bearingError: number
}

export class SelfChainModel {
  readonly chain = new ChainStoreV4(1, NETCODE_V4_MAX_SEGMENTS_PER_SNAKE)

  private x = 0
  private z = 0
  private bearing = 0
  private length = 0
  private flags = 0
  private spawnToken = 0
  private currentTick = 0
  private authoritativeLength = 0
  private boostFraction = 0
  private initialized = false
  private config: MovementConfigV4 = { ...DEFAULT_MOVEMENT_CONFIG_V4 }
  private readonly inputHistory: RecordedInputV4[] = []
  private readonly previousPositions = new Float32Array(NETCODE_V4_MAX_SEGMENTS_PER_SNAKE * 2)
  private previousCount = 0
  private previousX = 0
  private previousZ = 0
  private previousBearing = 0
  private previousLength = 0
  private previousFlags = 0
  private renderStateCaptured = false
  private displayX = 0
  private displayZ = 0
  private displayBearing = 0
  private readonly displayPositions = new Float32Array(NETCODE_V4_MAX_SEGMENTS_PER_SNAKE * 2)
  private displayCount = 0
  private displayInitialized = false

  initializeFromAssign(assign: DecodedAssignSnakeV4): void {
    this.spawnToken = assign.spawnToken >>> 0
    this.x = Math.fround(assign.x)
    this.z = Math.fround(assign.z)
    this.bearing = Math.fround(assign.bearing)
    this.length = assign.length
    this.authoritativeLength = assign.length
    this.boostFraction = 0
    this.flags = assign.flags
    this.currentTick = 0
    this.initialized = true
    this.inputHistory.length = 0
    this.chain.initSnake(0, this.x, this.z, this.length, this.bearing, snakeSegmentSpacingFromLength(this.length))
    this.capturePreviousState()
    this.renderStateCaptured = true
    this.snapDisplay()
  }

  setMovementConfig(partial: Partial<MovementConfigV4>): void {
    this.config = { ...this.config, ...partial }
  }

  isInitialized(): boolean {
    return this.initialized
  }

  getSpawnToken(): number {
    return this.spawnToken
  }

  getCurrentTick(): number {
    return this.currentTick >>> 0
  }

  prepareRenderFrame(): void {
    if (!this.renderStateCaptured) {
      this.capturePreviousState()
      this.renderStateCaptured = true
    }
  }

  markRenderFrameDone(): void {
    this.renderStateCaptured = false
  }

  getHead(): SelfHeadViewV4 {
    return {
      x: this.x,
      z: this.z,
      bearing: this.bearing,
      length: this.length,
      flags: this.flags,
    }
  }

  getRenderedHead(_alpha: number): SelfHeadViewV4 {
    return {
      x: this.displayX,
      z: this.displayZ,
      bearing: this.displayBearing,
      length: this.length,
      flags: this.flags,
    }
  }

  getRenderedChain(_alpha: number): ChainPointV4[] {
    const count = this.displayCount
    const points: ChainPointV4[] = []
    for (let i = 0; i < count; i++) {
      const offset = i * 2
      points.push({ x: this.displayPositions[offset], z: this.displayPositions[offset + 1] })
    }
    return points
  }

  copyRenderedChainTo(target: ChainPointV4[]): ChainPointV4[] {
    const count = this.displayCount
    for (let i = 0; i < count; i++) {
      const offset = i * 2
      let point = target[i]
      if (!point) {
        point = { x: 0, z: 0 }
        target[i] = point
      }
      point.x = this.displayPositions[offset]
      point.z = this.displayPositions[offset + 1]
    }
    target.length = count
    return target
  }

  updateDisplay(): void {
    if (!this.displayInitialized) {
      this.snapDisplay()
      return
    }
    const s = DISPLAY_SMOOTHING
    this.displayX = lerp(this.displayX, this.x, s)
    this.displayZ = lerp(this.displayZ, this.z, s)
    this.displayBearing = this.displayBearing + normalizeRadians(this.bearing - this.displayBearing) * s

    const simCount = this.chain.counts[0]
    const oldDisplayCount = this.displayCount
    this.displayCount = simCount
    // Snap new segments to simulation positions to avoid lerping from stale (0,0)
    for (let i = oldDisplayCount; i < simCount; i++) {
      const offset = i * 2
      this.displayPositions[offset] = this.chain.positions[offset]
      this.displayPositions[offset + 1] = this.chain.positions[offset + 1]
    }
    for (let i = 0; i < simCount; i++) {
      const offset = i * 2
      this.displayPositions[offset] = lerp(this.displayPositions[offset], this.chain.positions[offset], s)
      this.displayPositions[offset + 1] = lerp(this.displayPositions[offset + 1], this.chain.positions[offset + 1], s)
    }
  }

  snapDisplay(): void {
    this.displayX = this.x
    this.displayZ = this.z
    this.displayBearing = this.bearing
    this.displayCount = this.chain.counts[0]
    this.displayPositions.set(this.chain.positions.subarray(0, this.displayCount * 2))
    this.displayInitialized = true
  }

  recordInput(input: RecordedInputV4): void {
    this.inputHistory.push({
      seq: input.seq & 0xFFFF,
      inputTick: input.inputTick >>> 0,
      desiredHeading: Math.fround(normalizeRadians(input.desiredHeading)),
      flags: input.flags & 0xFF,
    })
    if (this.inputHistory.length > MAX_INPUT_HISTORY) {
      this.inputHistory.splice(0, this.inputHistory.length - MAX_INPUT_HISTORY)
    }
  }

  advanceToTick(targetTick: number): boolean {
    if (!this.initialized) return false

    const gap = signedUint32Delta(targetTick >>> 0, this.currentTick >>> 0)
    if (gap < 0 || gap > NETCODE_V4_MAX_CATCHUP_TICKS) return false

    this.replayRange(this.currentTick, targetTick >>> 0)
    return true
  }

  applySelfChainSnapshot(snapshot: DecodedSelfChainSnapshotV4): void {
    if (!this.initialized || snapshot.spawnToken !== this.spawnToken) return

    if (!this.renderStateCaptured) {
      this.capturePreviousState()
      this.renderStateCaptured = true
    }

    this.currentTick = snapshot.serverTick >>> 0
    this.x = Math.fround(snapshot.headX)
    this.z = Math.fround(snapshot.headZ)
    this.bearing = Math.fround(snapshot.bearing)
    this.length = snapshot.length
    this.authoritativeLength = snapshot.length
    this.boostFraction = 0
    this.flags = snapshot.flags
    this.dropAckedInputs(snapshot.ackSeq)
    this.chain.loadSnapshot(0, snapshot.points)
    this.chain.setCountFromLength(0, this.length)

    const latestTick = this.getLatestRecordedTick()
    if (latestTick > this.currentTick) {
      this.replayRange(this.currentTick, latestTick)
    }
  }

  applySelfHeadState(state: DecodedSelfHeadStateV4): ReconcileResultV4 {
    if (!this.initialized || state.spawnToken !== this.spawnToken) {
      return { kind: 'none', headError: 0, bearingError: 0 }
    }

    if (!this.renderStateCaptured) {
      this.capturePreviousState()
      this.renderStateCaptured = true
    }

    const previousPredicted = {
      x: this.x,
      z: this.z,
      bearing: this.bearing,
      flags: this.flags,
    }

    this.dropAckedInputs(state.ackSeq)

    const latestTick = this.getLatestRecordedTick()
    const targetTick = Math.max(state.serverTick >>> 0, latestTick)

    const authoritativeHead = {
      x: state.headX,
      z: state.headZ,
      bearing: normalizeRadians(state.bearing),
      length: state.length,
      flags: state.flags,
    }

    this.currentTick = state.serverTick >>> 0
    this.x = Math.fround(authoritativeHead.x)
    this.z = Math.fround(authoritativeHead.z)
    this.bearing = Math.fround(authoritativeHead.bearing)
    this.length = authoritativeHead.length
    this.authoritativeLength = authoritativeHead.length
    this.boostFraction = 0
    this.flags = authoritativeHead.flags
    this.chain.setCountFromLength(0, this.length)
    this.chain.positions[0] = this.x
    this.chain.positions[1] = this.z
    this.chain.resetPathFromPositions(0)

    if (targetTick > this.currentTick) {
      this.replayRange(this.currentTick, targetTick)
    }

    const headError = Math.hypot(this.x - previousPredicted.x, this.z - previousPredicted.z)
    const bearingError = Math.abs(normalizeRadians(this.bearing - previousPredicted.bearing))
    const nonInvulnerableMask = ~SnakeNetFlagsV4.INVULNERABLE
    const flagsMismatch =
      (this.flags & nonInvulnerableMask) !== (previousPredicted.flags & nonInvulnerableMask)

    if (flagsMismatch || headError > HARD_DRIFT_DISTANCE || bearingError > HARD_DRIFT_BEARING) {
      return { kind: 'resync', headError, bearingError }
    }
    if (headError > SOFT_DRIFT_DISTANCE || bearingError > SOFT_DRIFT_BEARING) {
      return { kind: 'soft-drift', headError, bearingError }
    }
    return { kind: 'continue', headError, bearingError }
  }

  private replayRange(fromTick: number, toTick: number): void {
    let tick = fromTick >>> 0
    let inputIndex = this.findReplayStartIndex(tick)
    let activeInput = this.getLastInputBeforeOrAt(tick)

    while (tick < (toTick >>> 0)) {
      const nextTick = (tick + 1) >>> 0
      while (inputIndex < this.inputHistory.length && this.inputHistory[inputIndex].inputTick <= nextTick) {
        activeInput = this.inputHistory[inputIndex]
        inputIndex++
      }

      this.step(activeInput)
      tick = nextTick
      this.currentTick = tick
    }
  }

  private step(activeInput: RecordedInputV4 | undefined): void {
    const desiredHeading = activeInput ? activeInput.desiredHeading : this.bearing
    const boost = activeInput ? (activeInput.flags & 0x01) !== 0 : (this.flags & BOOSTING_FLAG) !== 0

    this.chain.setCountFromLength(0, this.length)
    const nextBearing = limitedTurnV4(
      this.bearing,
      desiredHeading,
      this.config.baseTurnRate,
      NETCODE_V4_FIXED_DT_SECONDS,
    )
    this.bearing = Math.fround(nextBearing)
    const isBoosting = boost && combatLengthFromSnakeLength(this.length) > this.config.minBoostLength
    this.flags = isBoosting ? (this.flags | BOOSTING_FLAG) : (this.flags & ~BOOSTING_FLAG)

    const speed = calculateSpeedV4(combatLengthFromSnakeLength(this.length), isBoosting, this.config)
    this.x = Math.fround(this.x + Math.cos(nextBearing) * speed * NETCODE_V4_FIXED_DT_SECONDS)
    this.z = Math.fround(this.z + Math.sin(nextBearing) * speed * NETCODE_V4_FIXED_DT_SECONDS)
    this.chain.updateChain(0, this.x, this.z, snakeSegmentSpacingFromLength(this.length))

    if (!isBoosting) return

    this.boostFraction = Math.fround(this.boostFraction + this.config.boostSparkDropRate * NETCODE_V4_FIXED_DT_SECONDS)
    const trimCount = Math.floor(this.boostFraction)
    this.boostFraction = Math.fround(this.boostFraction - trimCount)
    if (trimCount <= 0) return

    const maxRemovable = Math.max(
      0,
      economicSparksFromSnakeLength(this.length) - economicSparksFromSnakeLength(this.authoritativeLength),
    )
    const removeCount = Math.min(trimCount, maxRemovable)
    if (removeCount <= 0) return

    this.length = Math.max(this.authoritativeLength, decreaseSnakeLengthBySparks(this.length, removeCount))
    this.chain.setCountFromLength(0, this.length)
  }

  private dropAckedInputs(ackSeq: number): void {
    let writeIndex = 0
    for (let readIndex = 0; readIndex < this.inputHistory.length; readIndex++) {
      const input = this.inputHistory[readIndex]
      if (this.isSeqAcked(input.seq, ackSeq)) continue
      this.inputHistory[writeIndex] = input
      writeIndex++
    }
    this.inputHistory.length = writeIndex
  }

  private isSeqAcked(seq: number, ackSeq: number): boolean {
    return ((ackSeq - seq + 0x10000) & 0xFFFF) < 0x8000
  }

  private findReplayStartIndex(tick: number): number {
    let index = 0
    while (index < this.inputHistory.length && this.inputHistory[index].inputTick <= tick) index++
    return index
  }

  private getLastInputBeforeOrAt(tick: number): RecordedInputV4 | undefined {
    for (let i = this.inputHistory.length - 1; i >= 0; i--) {
      if (this.inputHistory[i].inputTick <= tick) return this.inputHistory[i]
    }
    return undefined
  }

  private getLatestRecordedTick(): number {
    if (this.inputHistory.length === 0) return this.currentTick
    return this.inputHistory[this.inputHistory.length - 1].inputTick >>> 0
  }

  private capturePreviousState(): void {
    this.previousX = this.x
    this.previousZ = this.z
    this.previousBearing = this.bearing
    this.previousLength = this.length
    this.previousFlags = this.flags
    this.previousCount = this.chain.counts[0]
    this.previousPositions.set(this.chain.positions.subarray(0, this.previousCount * 2))
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 1
  return Math.max(0, Math.min(1, value))
}
